import { useState, useEffect, useRef } from "react";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import OTPInput from "otp-input-react";
import { CookiesProvider, useCookies } from "react-cookie";

import {
  Box,
  Container,
  Typography,
  TextField,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Button,
  Link as MUILink,
  Stack,
} from "@mui/material";

import LoginFooter from "../components/loginFooter";

import { __API_URL__ } from "../SERVER_URL";
import useUser from "../hooks/user";
import logo from "../assets/img/smartTrail.svg";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [otpState, setOtpState] = useState("sending_otp");
  const [showPassword, setShowPassword] = useState(false);
  const [OTP, setOTP] = useState("");
  const [resendButtonDisabled, setResendButtonDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState("");
  const [isDisable, setIsDisable] = useState(false);
  const [cookies, setCookie] = useCookies(["Trust_Browser"]);
  const [securityInfo, setSecurityInfo] = useState();
  const [isPatient, setIsPatient] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const logUserAction = async (actionType, trialId, patientId, details) => {
    const token = localStorage.getItem("access_token");
    await axios.post(
      `${__API_URL__}/api/appiell/user_actions_logs/`,
      {
        action_type: actionType,
        trial_id: trialId,
        patient_id: patientId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const user = useUser();
  const validateEmail = async (e) => {
    e.preventDefault();
    const re =
      /^(([^<>()[\],;:\s@]+([^<>()[\],;:\s@]+)*)|(.+))@(([^<>()[\],;:\s@]+)+[^<>()[\],;:\s@]{2,})$/i;

    if (email.match(re)) {
      setError("");
      let loginCred = {
        email,
        password: pass,
        isPatient: isPatient,
      };

      try {
        let response = await user.actions.login(loginCred);
        localStorage.setItem("email", email);

        if (response) {
          const testEmails = process.env.REACT_APP_KATALON_TEST_EMAIL?.split(",").map((e) =>
            e.trim()
          );

          if (testEmails?.includes(email)) {
            const newToken = localStorage.getItem("token");

            const storeBrowserCookie = cookies["Trust_Browser"];
            trustBrowserCookie(storeBrowserCookie, newToken);

            setOtpState("Remember_Device");
          } else {
            const newToken = localStorage.getItem("token");
            setToken(newToken);
            localStorage.removeItem("token");

            const storeBrowserCookie = cookies["Trust_Browser"];
            trustBrowserCookie(storeBrowserCookie, newToken);

            setOtpState("otp_verification");
          }
        } else {
          toast.error(error.response?.data?.message);
        }
      } catch (error) {
        console.log(">>", error);
        setError("An error occurred during login");
      }
    } else {
      toast.error("Please provide a valid email address");
    }
  };

  const sendVerificationEmail = () => {
    const data = {
      email: email,
    };
    const endpoint = isPatient ? "/api/patient/auth/get-pincode" : "/api/auth/staff/send_otp/";

    setIsDisable(true);
    axios
      .post(`${__API_URL__}${endpoint}`, data)
      .then((response) => {
        toast.success(response.data.message);
        setOtpState("password");
        setResendButtonDisabled(true);
        startResendTimer(60);
      })
      .catch((err) => {
        toast.error(err.response.data.message);
      })
      .finally(() => {
        setIsDisable(false);
      });
  };

  const saveToLocalStorage = () => {
    localStorage.setItem("otpState", otpState);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    verifyOTP();
    localStorage.removeItem("otpState");
  };

  const handleResend = (e) => {
    e.preventDefault();
    sendVerificationEmail();
  };

  const verifyOTP = () => {
    const endpoint = isPatient ? "/api/patient/auth/verify-pincode" : "/api/auth/staff/verify_otp/";

    const key = isPatient ? "pin" : "token";

    const data = {
      email: email,
      [key]: OTP,
    };

    axios
      .post(`${__API_URL__}${endpoint}`, data)
      .then((res) => {
        localStorage.setItem("token", token);
        toast.success("Successfully Verified");
        setOtpState("Remember_Device");
        logUserAction("login", "-", "-");
        if (isPatient) {
          localStorage.setItem("token", res.data.token);
        }
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const rememberDevice = async (e) => {
    e.preventDefault();

    setCookie("Trust_Browser", email, {
      maxAge: securityInfo?.remember_days * 86400,
    });
    user.actions.setIsLoginAction(true);
    localStorage.removeItem("otpState");
  };
  const AdminSecurity = () => {
    const security = {
      remember_days: securityInfo,
      valid_login_session_days: securityInfo,
    };
    axios.get(`${__API_URL__}/api/security/admin_security/`, security).then((res) => {
      setSecurityInfo(res.data);
    });
  };

  useEffect(() => {
    AdminSecurity();
  }, []);

  const notRememberDevice = async (e) => {
    e.preventDefault();

    user.actions.setIsLoginAction(true);
    localStorage.removeItem("otpState");
  };

  const trustBrowserCookie = (storeBrowserCookie, newToken) => {
    if (storeBrowserCookie) {
      localStorage.setItem("token", newToken);
      user.actions.setIsLoginAction(true);
      localStorage.removeItem("otpState");
    } else setOtpState("otp_verification");
  };

  useEffect(() => {
    const storedOtpState = localStorage.getItem("otpState");
    const storedEmailState = localStorage.getItem("email");
    const storedTimerState = localStorage.getItem("resendTimer");

    if (storedOtpState) {
      setOtpState(storedOtpState);
      setEmail(storedEmailState || "");
      setResendTimer(storedTimerState);
      if (storedTimerState > 0) {
        setResendButtonDisabled(true);
        startResendTimer(storedTimerState);
      }
    }
  }, []);

  useEffect(() => {
    if (
      otpState === "sending_otp" ||
      otpState === "otp_verification" ||
      otpState === "password" ||
      otpState === "Remember_Device"
    ) {
      saveToLocalStorage();
    }
    if (otpState === "Remember_Device") {
      clearInterval(intervalRef.current);
      setResendTimer(0);
      localStorage.removeItem("resendTimer");
    }
  }, [otpState]);

  const intervalRef = useRef(null);

  const startResendTimer = (timerInMinutes) => {
    setResendTimer(timerInMinutes);
    intervalRef.current = setInterval(() => {
      setResendTimer((prevTimer) => (prevTimer > 0 ? prevTimer - 1 : 0));
    }, 1000);
    setTimeout(() => {
      clearInterval(intervalRef.current);
      setResendButtonDisabled(false);
    }, timerInMinutes * 1000);
  };

  useEffect(() => {
    if (resendTimer) {
      localStorage.setItem("resendTimer", resendTimer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (isPatient ? OTP.length === 5 : OTP.length === 6) {
      handleVerify({ preventDefault: () => {} });
    }
  }, [OTP]);

  return (
    <Box
      sx={{
        bgcolor: "#fcf6f6",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CookiesProvider>
        <Container
          maxWidth="sm"
          sx={{
            flex: "1 0 auto",
            display: "flex",
            py: 0,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              bgcolor: "background.paper",
              boxShadow: 3,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="SmartTrail"
              sx={{ height: 48, display: "block", mb: 4 }}
            />

            {otpState === "sending_otp" ? (
              <Box
                component="form"
                onSubmit={validateEmail}
                noValidate
                sx={{ display: "grid", gap: 2 }}
              >
                <TextField
                  type="email"
                  value={email}
                  placeholder="Email"
                  label="Email"
                  fullWidth
                  onChange={(e) => setEmail(e.target.value)}
                  inputProps={{ "data-testid": "login-email-input" }}
                />

                {!isPatient && (
                  <>
                    <OutlinedInput
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={pass}
                      onChange={(e) => setPass(e.target.value)}
                      fullWidth
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      }
                      sx={{ "& input": { py: 1.5 } }}
                      inputProps={{ "data-testid": "login-password-input" }}
                    />

                    {!!error && (
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    )}
                  </>
                )}

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  {!isPatient && (
                    <MUILink href="/forgot_password" underline="hover" sx={{ fontWeight: 600 }}>
                      Forgot Password?
                    </MUILink>
                  )}
                  <MUILink
                    component="button"
                    type="button"
                    onClick={() => setIsPatient((prev) => !prev)}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    {!isPatient ? "Login as Patient" : "Not a Patient?"}
                  </MUILink>
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    data-testid="login-submit-btn"
                  >
                    Secure Sign In
                  </Button>
                </Box>
              </Box>
            ) : otpState === "otp_verification" ? (
              <Stack spacing={3}>
                <Typography>To protect your security, we need to verify your identity.</Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button variant="contained" onClick={handleResend} disabled={isDisable}>
                    Email me: {email}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button variant="outlined" onClick={() => setOtpState("sending_otp")}>
                    Back to Login
                  </Button>
                </Box>
              </Stack>
            ) : otpState === "password" ? (
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  {/* Keep your OTPInput as-is; wrap with Box for spacing */}
                  <OTPInput
                    value={OTP}
                    onChange={setOTP}
                    autoFocus
                    OTPLength={isPatient ? 5 : 6}
                    otpType="number"
                    disabled={false}
                    secure={false}
                  />
                </Box>

                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Button
                    variant="text"
                    onClick={handleResend}
                    disabled={resendButtonDisabled || isDisable}
                  >
                    Resend OTP {resendButtonDisabled ? `(${resendTimer}s)` : ""}
                  </Button>
                  <Button variant="contained" onClick={handleVerify}>
                    Verify
                  </Button>
                </Stack>
              </Stack>
            ) : otpState === "Remember_Device" ? (
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                  Access Code Accepted
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={notRememberDevice}
                    data-testid="skip-remember-device-btn"
                  >
                    Do Not Remember Device
                  </Button>
                  <Button
                    variant="contained"
                    onClick={rememberDevice}
                    data-testid="remember-device-btn"
                  >
                    Remember Device
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Box>
        </Container>
      </CookiesProvider>

      <LoginFooter />
    </Box>
  );
};

export default LoginPage;
